/**
 * Copyright 2026 Ziz Academy
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, BrowserRouter } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navbar } from './components/Navbar';
import Login from './pages/Login';
import Register from './pages/Register';
import StudentDashboard from './pages/StudentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import AdminDashboard from './pages/AdminDashboard';
import TakeExam from './pages/TakeExam';
import ExamResult from './pages/ExamResult';
import CreateExam from './pages/CreateExam';
import EditExam from './pages/EditExam';
import EvaluateSubmissions from './pages/EvaluateSubmissions';
import ExamPreview from './pages/ExamPreview';
import MySubmissions from './pages/MySubmissions';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Evaluations from './pages/Evaluations';
import AssignExam from './pages/AssignExam';
import Questions from './pages/Questions';
import './styles/App.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRole?: string; allowedRoles?: string[] }> = ({
    children,
    allowedRole,
    allowedRoles,
}) => {
    const { user } = useAuth();

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && allowedRoles.length > 0) {
        if (!allowedRoles.includes(user.role)) {
            return <Navigate to="/" replace />;
        }
    } else if (allowedRole && user.role !== allowedRole) {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
};

const AppRoutes: React.FC = () => {
    const { user } = useAuth();

    return (
        <Routes>
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
            <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />

            <Route
                path="/"
                element={
                    user ? (
                        user.role === 'TEACHER' ? (
                            <TeacherDashboard />
                        ) : user.role === 'ADMIN' || user.role === 'SUPER_ADMIN' ? (
                            <AdminDashboard />
                        ) : (
                            <StudentDashboard />
                        )
                    ) : (
                        <Home />
                    )
                }
            />

            <Route
                path="/exam/:examId"
                element={
                    <ProtectedRoute allowedRole="STUDENT">
                        <TakeExam />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/result/:submissionId"
                element={
                    <ProtectedRoute>
                        <ExamResult />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/my-submissions"
                element={
                    <ProtectedRoute allowedRole="STUDENT">
                        <MySubmissions />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/create-exam"
                element={
                    <ProtectedRoute allowedRoles={["TEACHER", "ADMIN", "SUPER_ADMIN"]}>
                        <CreateExam />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/edit-exam/:examId"
                element={
                    <ProtectedRoute allowedRoles={["TEACHER", "ADMIN", "SUPER_ADMIN"]}>
                        <EditExam />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/evaluate/:examId"
                element={
                    <ProtectedRoute allowedRoles={["TEACHER", "ADMIN", "SUPER_ADMIN"]}>
                        <EvaluateSubmissions />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/assign-exam/:examId"
                element={
                    <ProtectedRoute allowedRoles={["TEACHER", "ADMIN", "SUPER_ADMIN"]}>
                        <AssignExam />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/preview-exam/:examId"
                element={
                    <ProtectedRoute allowedRoles={["TEACHER", "ADMIN", "SUPER_ADMIN"]}>
                        <ExamPreview />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/questions"
                element={
                    <ProtectedRoute allowedRoles={["TEACHER", "ADMIN", "SUPER_ADMIN"]}>
                        <Questions />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/evaluations"
                element={
                    <ProtectedRoute allowedRoles={["TEACHER", "ADMIN", "SUPER_ADMIN"]}>
                        <Evaluations />
                    </ProtectedRoute>
                }
            />

            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

const App: React.FC = () => {
    return (
        <BrowserRouter>
            <AuthProvider>
                <div className="app">
                    <AppContent />
                </div>
            </AuthProvider>
        </BrowserRouter>
    );
};

const AppContent: React.FC = () => {
    const { user } = useAuth();

    return (
        <>
            <Navbar />
            <main className={user ? 'main-content' : ''}>
                <AppRoutes />
            </main>
        </>
    );
};

export default App;